import { IMeasurementData } from "api/interfaces";
import MeasurementRowModel from "api/models/MeasurementRowModel";
import { decimal, valueToPercentual } from "utils/DecimalPrecision";

const defaultData = { total: 0, quantity: 0, percentual: 0 };

class CalculateMeasurementRowService {
  /** Atualiza os valores da linha da medição atual com os dados
   * informados, passando os mesmos por tratamento para evitar valores
   * incorretos e depois recalculando os valores acumulados e a faturar.
   * @returns {IMeasurementData} Retorna os valores antigos da linha
   */
  public calculateMeasurementValues(
    measurementRow: MeasurementRowModel,
    currentMeasurement: {
      total: number;
      quantity?: number;
      percentual?: number;
    }
  ): IMeasurementData {
    const oldMeasurement = measurementRow.currentMeasurement;

    const calculatedData = this.calculateCurrentMeasurement(
      measurementRow,
      currentMeasurement
    );
    measurementRow.setCurrentMeasurement(calculatedData);

    const measuredData = this.calculateAccumulatedValues(measurementRow);
    measurementRow.setAccumulatedMeasurement(measuredData);

    const billingData = this.calculateBillingValues(measurementRow);
    measurementRow.setBillingMeasurement(billingData);

    return oldMeasurement;
  }

  /** Trata e calcula os valores medidos da medição atual. */
  private calculateCurrentMeasurement(
    measurementRow: MeasurementRowModel,
    { total, quantity = 0, percentual = 0 }
  ) {
    const rowData = measurementRow.contractRow;
    if (rowData) {
      const rowTotal = rowData.total;
      const rowQuantity = rowData.quantity;

      // Verifica se o valor é próximo de 0, caso seja, o valor é alterado para 0
      // faz o mesmo com o saldo contratado da linha
      total = decimal(total, 2).setToMaxOrMin(rowTotal, 0);
      quantity = decimal(quantity).setToMaxOrMin(rowQuantity, 0);
      percentual = this.calculatePercentual(total, rowTotal);
    }

    return {
      total,
      quantity,
      percentual,
    };
  }

  /** Calcula os valores de quantidade, total e porcetagem acumulados
   * da linha, somando os valores medidos da linha com os valores
   * acumulados antigos - que não incluem os valores da medição atual.
   */
  private calculateAccumulatedValues(measurementRow: MeasurementRowModel) {
    const rowData = measurementRow.contractRow;
    if (!rowData) return { ...defaultData };

    const rowTotal = rowData.total;
    const rowQuantity = rowData.quantity;
    const measured = measurementRow.getRowMeasuredWithoutCurrentMeasurement();

    let quantity = decimal(measured.quantity || 0)
      .sum(measurementRow.quantity)
      .toNumber();
    let total = decimal(measured.total).sum(measurementRow.total).toNumber();

    // Verifica se o valor é próximo de 0, caso seja, o valor é alterado para 0
    // faz o mesmo com o saldo contratado da linha
    total = decimal(total, 2).setToMaxOrMin(rowTotal, 0);
    quantity = decimal(quantity).setToMaxOrMin(rowQuantity, 0);

    return {
      total,
      quantity,
      percentual: this.calculatePercentual(total, rowTotal),
    };
  }

  /** Calcula os valores de quantidade, total e porcetagem a serem faturados
   * da linha, subtraindo o valor contratado (Que está no contrato) do valor
   * medido acumulado para aquela linha.
   */
  private calculateBillingValues(measurementRow: MeasurementRowModel) {
    const rowData = measurementRow.contractRow;
    if (!rowData) return { ...defaultData };

    const rowTotal = rowData.total;
    const rowQuantity = rowData.quantity;
    const measured = measurementRow.measured;

    let quantity = calculate(measured.quantity, rowQuantity);
    let total = calculate(measured.total, rowTotal, 2);
    let percentual = 0;

    if (rowTotal !== 0) {
      percentual = decimal(measured.total, 2).isEqualTo(0)
        ? 100
        : this.calculatePercentual(total, rowTotal);
    }

    return { total, quantity, percentual };

    function calculate(value, rowValue, precision?) {
      return decimal(value, precision).isEqualTo(rowValue)
        ? 0
        : decimal(rowValue).subtract(value).toNumber();
    }
  }

  private calculatePercentual(total: number, rowTotal: number): number {
    if (decimal(rowTotal, 2).isEqualTo(0)) return 0;
    if (decimal(total, 2).isEqualTo(rowTotal)) return 100;
    return valueToPercentual(total, rowTotal);
  }
}

export default new CalculateMeasurementRowService();
